import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Form, FormActions, FormInput, FormDatePicker, FormSelect, FormButton } from '../../../components/form/index.js';
import { useUsersContext } from '../../../contexts/users.tsx';
import { UserRepresentation } from '../../../models/users/userRepresentation.ts';

const validationSchema = Yup.object().shape({
    userName: Yup.string().required('Username is required').min(2, 'Username is too short').max(255, 'Username is too long'),
    firstName: Yup.string().required('First name is required').min(2, 'First name is too short').max(255, 'First name is too long'),
    lastName: Yup.string().required('Last name is required').min(2, 'Last name is too short').max(255, 'Last name is too long'),
    email: Yup.string().required('Email is required').min(5, 'Email is too short').max(255, 'Email is too long'),
    phone: Yup.string().nullable(),
    birthDate: Yup.date().nullable(),
    sex: Yup.string().nullable()
});

const sexOptions = [
    { name: 'Prefer not to say', sex: '' },
    { name: 'M', sex: 'M' },
    { name: 'F', sex: 'F' }
]

const ProfileBody = () => {
    const { profile, fetchProfile, updateProfile } = useUsersContext();
    const [localProfile, setLocalProfile] = useState<UserRepresentation>(profile);
    useEffect(() => {
        if (!profile)
            fetchProfile();
        else
            setLocalProfile(profile);
    }, [profile])
    const navigate = useNavigate();
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setLocalProfile((prevData) => ({
            ...prevData,
            [id]: value, // Update the corresponding field in state
        }));

        validationSchema
            .validateAt(id, { ...localProfile, [id]: value })
            .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
            .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
    };

    const handleSubmit = () => {
        validationSchema
            .validate(localProfile, { abortEarly: false })
            .then(() => {
                updateProfile(localProfile);
            })
            .catch((err) => {
                const validationErrors: Record<string, string> = {};
                err.inner.forEach((error: Yup.ValidationError) => {
                    if (error.path) validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
            });
    };
    return (
        <Form>
            <FormInput
                entity={localProfile}
                handleInputChange={handleInputChange}
                fieldName='userName'
                label='Username'
                errors={errors}
            />
            <FormInput
                entity={localProfile}
                handleInputChange={handleInputChange}
                fieldName='firstName'
                label='First name'
                errors={errors}
            />
            <FormInput
                entity={localProfile}
                handleInputChange={handleInputChange}
                fieldName='lastName'
                label='Last name'
                errors={errors}
            />
            <FormInput
                entity={localProfile}
                handleInputChange={handleInputChange}
                fieldName='email'
                label='Email'
                errors={errors}
            />
            <FormInput
                entity={localProfile}
                handleInputChange={handleInputChange}
                fieldName='phone'
                label='Phone'
                errors={errors}
            />
            <FormDatePicker
                entity={localProfile}
                handleInputChange={handleInputChange}
                fieldName='birthDate'
                label='Birth Date'
                errors={errors}
            />
            <FormSelect
                entity={localProfile}
                options={sexOptions}
                handleInputChange={handleInputChange}
                fieldName='sex'
                label='Sex'
                errors={errors}
            />
            <FormActions
                mainAction={handleSubmit}
                mainActionText={"Save"}
                showCancelButton
            />
        </Form>
    );
};

export default ProfileBody;